import { cartItemReferenceStoreActions } from './cart-item-reference-store.actions';
import type { CartItemReference } from '../models';
import type { Observable } from '@legendapp/state';
import { cartItemReferenceStore$, getCartItemReferenceSyncStatus } from './cart-item-reference.store';

/**
 * PUBLIC API FOR CART ITEM REFERENCE STATE ACCESS
 *
 * This is the ONLY way external code should access cart item reference state.
 * Direct access to cartItemReferenceStore$ is FORBIDDEN and will be removed.
 *
 * ARCHITECTURE PRINCIPLES:
 * - Encapsulation: Internal state is hidden behind getters
 * - Single Responsibility: Each function has a clear purpose
 * - Controlled Access: State mutations only through actions
 * - Type Safety: All functions are properly typed
 */

// ============================================================================
// CART ITEM REFERENCE STATE GETTERS - READ-ONLY ACCESS
// ============================================================================

export const cartItemReferenceStoreStateAPI = {
    // Store identification
    getStoreId: (): string => cartItemReferenceStoreActions.getStoreId(),
    // Items access - immutable
    getItems: (): ReadonlyArray<CartItemReference> => cartItemReferenceStoreActions.getItems(),

    // Complete state - immutable snapshot
    getState: () => {
        const state = cartItemReferenceStoreActions.getState();
        return Object.freeze({
            ...state,
            items: Object.freeze(state.items),
        });
    },

    // Observable access for reactive updates (advanced use cases)
    getItemsObservable: (): Observable<CartItemReference[]> =>
        cartItemReferenceStore$.items as Observable<CartItemReference[]>,

    // Store observable for sync operations (should only be used with syncState)
    getStoreObservable: () => cartItemReferenceStore$,

    // quantityStepper access
    getQuantityStepper: (): number => cartItemReferenceStoreActions.getQuantityStepper(),
    getQuantityStepperObservable: (): Observable<number> => cartItemReferenceStore$.quantityStepper,
} as const;

// ============================================================================
// COMPUTED VALUES - READ-ONLY ACCESS
// ============================================================================

export const cartItemReferenceStoreComputedAPI = {
    // Item counts
    getTotalReferences: (): number => cartItemReferenceStoreActions.getItems().length,

    getTotalQuantity: (): number => {
        return cartItemReferenceStoreActions.getItems().reduce((total, ref) => total + ref.qty, 0);
    },

    // State checks
    isEmpty: (): boolean => cartItemReferenceStoreActions.getItems().length === 0,

    hasReferences: (): boolean => cartItemReferenceStoreActions.getItems().length > 0,

    // Find references
    findReferenceById: (cartItemReferenceId: string): CartItemReference | undefined => {
        return cartItemReferenceStoreActions.getItems().find(ref => ref.cartItemReferenceId === cartItemReferenceId);
    },

    findReferencesByProductId: (productId: number): ReadonlyArray<CartItemReference> => {
        return cartItemReferenceStoreActions.getItems().filter(ref => ref.productId === productId);
    },

    findReferencesByCartItemId: (cartItemId: string): ReadonlyArray<CartItemReference> => {
        return cartItemReferenceStoreActions.getItems().filter(ref => ref.cartItemId === cartItemId);
    },

    // Selected references
    getSelectedReferences: (): ReadonlyArray<CartItemReference> => {
        return cartItemReferenceStoreActions.getItems().filter(ref => ref.isSelected);
    },

    hasSelectedReferences: (): boolean => {
        return cartItemReferenceStoreActions.getItems().some(ref => ref.isSelected);
    },

    // Sync status
    getSyncStatus: () => getCartItemReferenceSyncStatus(),
} as const;

// ============================================================================
// CART ITEM REFERENCE ACTIONS - CONTROLLED MUTATIONS
// ============================================================================

export const cartItemReferenceStoreMutationAPI = {
    // Store identification mutations
    setStoreId: (id: string): void => cartItemReferenceStoreActions.setStoreId(id),

    // Items mutations
    setItems: (items: CartItemReference[]): void => cartItemReferenceStoreActions.setItems(items),
    addItem: (item: CartItemReference): void => cartItemReferenceStoreActions.addItem(item),
    updateItem: (item: CartItemReference): void => cartItemReferenceStoreActions.updateItem(item),
    removeItem: (cartItemReferenceId: string): void => cartItemReferenceStoreActions.removeItem(cartItemReferenceId),
    removeItemsByCartId: (cartId: string): void => cartItemReferenceStoreActions.removeItemsByCartId(cartId),

    // quantityStepper mutation
    setQuantityStepper: (value: number): void => cartItemReferenceStoreActions.setQuantityStepper(value),

    // Bulk operations
    resetStore: (): void => cartItemReferenceStoreActions.resetStore(),
    updateTimestamp: (): void => cartItemReferenceStoreActions.updateTimestamp(),
} as const;

// ============================================================================
// UNIFIED PUBLIC API - SINGLE IMPORT POINT
// ============================================================================

/**
 * UNIFIED CART ITEM REFERENCE API
 *
 * This is the ONLY interface external code should use for cart item reference operations.
 *
 * USAGE EXAMPLES:
 * ```typescript
 * // READ OPERATIONS
 * const cartId = cartItemReferenceAPI.state.getCartId();
 * const items = cartItemReferenceAPI.state.getItems();
 * const isEmpty = cartItemReferenceAPI.computed.isEmpty();
 * const reference = cartItemReferenceAPI.computed.findReferenceById('ref-123');
 *
 * // WRITE OPERATIONS
 * cartItemReferenceAPI.actions.addItem(newReference);
 * cartItemReferenceAPI.actions.setCartId('cart-456');
 * cartItemReferenceAPI.actions.resetStore();
 *
 * // REACTIVE ACCESS (Advanced)
 * const items$ = cartItemReferenceAPI.state.getItemsObservable();
 * ```
 */
export const cartItemReferenceStoreAPI = {
    // Read-only state access
    state: cartItemReferenceStoreStateAPI,
    computed: cartItemReferenceStoreComputedAPI,

    // Controlled mutations
    actions: cartItemReferenceStoreMutationAPI,
} as const;

// ============================================================================
// TYPE EXPORTS FOR EXTERNAL USE
// ============================================================================

export type CartItemReferenceStoreStateAPI = typeof cartItemReferenceStoreStateAPI;
export type CartItemReferenceStoreComputedAPI = typeof cartItemReferenceStoreComputedAPI;
export type CartItemReferenceStoreMutationAPI = typeof cartItemReferenceStoreMutationAPI;
export type CartItemReferenceStoreAPI = typeof cartItemReferenceStoreAPI;
